import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import "./CryptoTable.css";

function CryptoTable() {
  const [cryptos, setCryptos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let intervalId;

    const fetchCryptos = async () => {
      setLoading(true); // Show loading message while fetching
      try {
        const response = await axios.get(
          "https://api.coingecko.com/api/v3/coins/markets",
          {
            params: {
              vs_currency: "usd",
              order: "market_cap_desc",
              per_page: 100,
              page: 1,
              sparkline: false,
              price_change_percentage: "24h",
            },
          }
        );
        setCryptos(response.data);
        setError(null); // Clear error if data fetch is successful
      } catch (err) {
        setError("Failed to load cryptocurrency data.");
        console.error(err);
      } finally {
        setLoading(false); // Hide loading message after fetch
      }
    };

    // Initial fetch
    fetchCryptos();

    // Set up interval for auto-refresh every 5 minutes
    intervalId = setInterval(fetchCryptos, 5 * 60 * 1000);

    // Cleanup interval on component unmount
    return () => clearInterval(intervalId);
  }, []);

  if (loading) {
    return <div className="loading-message">Loading cryptocurrencies...</div>;
  }

  if (error) {
    return <div className="error-message">{error}</div>;
  }

  if (cryptos.length === 0) {
    return <div className="empty-message">No cryptocurrencies found.</div>;
  }

  return (
    <div className="crypto-table-container">
      {/* Table layout for larger screens */}
      <table className="crypto-table">
        <thead>
          <tr>
            <th>#</th>
            <th>Coin</th>
            <th>Price</th>
            <th>Change % (24h)</th>
            <th>Market Cap</th>
            <th>Volume (24h)</th>
            <th>Circulating Supply</th>
          </tr>
        </thead>
        <tbody>
          {cryptos.map((crypto, index) => (
            <tr key={crypto.id}>
              <td>{index + 1}</td>
              <td>
                <Link to={`/crypto/${crypto.id}`} className="crypto-link">
                  <img
                    src={crypto.image}
                    alt={`${crypto.name} logo`}
                    className="crypto-logo"
                  />
                  <span>{crypto.name}</span>
                </Link>
              </td>
              <td>${crypto.current_price.toFixed(2)}</td>
              <td
                className={
                  crypto.price_change_percentage_24h > 0 ? "positive" : "negative"
                }
              >
                {crypto.price_change_percentage_24h.toFixed(2)}%
              </td>
              <td>${(crypto.market_cap / 1e9).toFixed(2)}B</td>
              <td>${(crypto.total_volume / 1e9).toFixed(2)}B</td>
              <td>{crypto.circulating_supply.toLocaleString()}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Card layout for smaller screens */}
      <div className="crypto-cards">
        {cryptos.map((crypto, index) => (
          <div className="crypto-card" key={crypto.id}>
            <img
              src={crypto.image}
              alt={`${crypto.name} logo`}
              className="crypto-logo"
            />
            <div className="crypto-name">{crypto.name}</div>
            <div className="crypto-price">${crypto.current_price.toFixed(2)}</div>
            <div
              className={`crypto-change ${
                crypto.price_change_percentage_24h > 0 ? "positive" : "negative"
              }`}
            >
              {crypto.price_change_percentage_24h.toFixed(2)}%
            </div>
            <div className="crypto-market-cap">
              Market Cap: ${(crypto.market_cap / 1e9).toFixed(2)}B
            </div>
            <div className="crypto-volume">
              Volume (24h): ${(crypto.total_volume / 1e9).toFixed(2)}B
            </div>
            <div className="crypto-supply">
              Supply: {crypto.circulating_supply.toLocaleString()}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default CryptoTable;
